import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import ScreenTransition from './ScreenTransition';

function TermsOfService() {
  const navigate = useNavigate();
  return (
    <ScreenTransition direction="right">
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-zinc-900/60 px-4 py-6 sm:p-8 rounded-2xl max-w-4xl w-full overflow-y-auto max-h-[90vh]">
          <div className="mb-6 flex flex-col items-center">
            <Button onClick={() => navigate('/')} className="w-32 mb-6">
              Back
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6 text-yellow-300">
              Terms of Service
            </h1>
            <p className="text-gray-400 text-sm text-center mb-8">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          <div className="space-y-6 text-gray-200 text-sm sm:text-base leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Introduction</h2>
              <p className="mb-3">
                Welcome to Wall Go. These Terms of Service ("Terms") govern your access to and use of the Wall Go website and game application (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
              </p>
              <p>
                If you do not agree to these Terms, please do not access or use the Service. We reserve the right to modify these Terms at any time, and your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Acceptance of Terms</h2>
              <p className="mb-3">
                By accessing, browsing, or using Wall Go in any manner, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and all applicable laws and regulations.
              </p>
              <p>
                If you do not agree with any part of these Terms, you must not access or use the Service. These Terms apply to all visitors, users, and others who access or use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Description of Service</h2>
              <p className="mb-3">
                Wall Go is a browser-based strategic board game inspired by "The Devil's Plan" (데블스 플랜), combining elements of the ancient game of Go/Baduk/Weiqi with wall-building mechanics. The Service is provided free of charge and operates entirely within your web browser.
              </p>
              <p>
                We reserve the right to modify, suspend, or discontinue the Service or any part thereof at any time, with or without notice, for any reason or no reason.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Free Use and Open Source</h2>
              <p className="mb-3">
                Wall Go is provided free of charge. You are welcome to use, copy, modify, and distribute the application for any purpose, including commercial use. However, if you use or modify Wall Go, we request that you include appropriate credit to "wallbad.uk".
              </p>
              <p>
                While the Service is free to use, we reserve all rights not expressly granted to you in these Terms. The game mechanics, design, and code remain the intellectual property of the creators.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">User Conduct</h2>
              <p className="mb-3">
                You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-2 ml-4">
                <li>Use the Service in any way that violates any applicable local, state, national, or international law or regulation</li>
                <li>Attempt to gain unauthorized access to any portion of the Service or any related systems or networks</li>
                <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
                <li>Use any automated system, including robots, spiders, or scrapers, to access the Service</li>
                <li>Reproduce, duplicate, copy, sell, or exploit any portion of the Service for commercial purposes without proper attribution</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Intellectual Property</h2>
              <p className="mb-3">
                The Service, including its original content, features, functionality, game design, graphics, and code, is owned by Wall Go and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p>
                While you are free to use and modify the Service, you may not claim ownership of the original work or remove attribution. Any modifications you create are your own, but the underlying intellectual property rights remain with the original creators.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Disclaimer of Warranties</h2>
              <p className="mb-3">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
              </p>
              <p>
                We do not warrant that the Service will be uninterrupted, secure, or error-free, or that defects will be corrected. We do not warrant or make any representations regarding the use or the results of the use of the Service in terms of its correctness, accuracy, reliability, or otherwise.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Limitation of Liability</h2>
              <p className="mb-3">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL WALL GO, ITS CREATORS, AFFILIATES, AGENTS, DIRECTORS, EMPLOYEES, OR LICENSORS BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY DAMAGES, INCLUDING WITHOUT LIMITATION DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO THE USE OF, OR INABILITY TO USE, THE SERVICE.
              </p>
              <p>
                Some jurisdictions do not allow the exclusion of certain warranties or the limitation or exclusion of liability for incidental or consequential damages. Accordingly, some of the above limitations may not apply to you.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Termination</h2>
              <p className="mb-3">
                We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
              <p>
                Upon termination, your right to use the Service will immediately cease. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Governing Law</h2>
              <p>
                These Terms shall be interpreted and governed by the laws of the jurisdiction in which the Service is operated, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Severability</h2>
              <p>
                If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. These Terms constitute the entire agreement between us regarding our Service and supersede and replace any prior agreements we might have between us regarding the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Changes to Terms</h2>
              <p className="mb-3">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
              </p>
              <p>
                What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Contact Information</h2>
              <p className="mb-3">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <p>
                <a
                  href="mailto:tete.dans.les.clouds@gmail.com"
                  className="text-yellow-300 hover:text-yellow-400 underline transition-colors"
                >
                  tete.dans.les.clouds@gmail.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </ScreenTransition>
  );
}

export default TermsOfService;
